import React from "react";
import { useTranslations } from "use-intl";
import TitledCard from "@/components/common/TitledCard";
import { Tabs } from "@/components/ui/tabs";
import { BankAccountsStatus, Invoice, Filters, InvoiceStatus } from "@/types/payments";
import { formatCurrency } from "@/utils/paymentUtils";
import { useStatusBadge } from "@/i18n/useStatusBadge";
import TabsNavigation from "./tabs/TabsNavigation";
import TabContent from "./tabs/TabContent";
import EditPaymentDialog from "./EditPaymentDialog";
import { useInvoiceActions } from "@/hooks/useInvoiceActions";
import { useAccountPayablesFiltering } from "@/hooks/useAccountPayablesFiltering";

interface AccountPayablesContentProps {
  invoices: Invoice[];
  setInvoices: React.Dispatch<React.SetStateAction<Invoice[]>>;
  selectedInvoices: string[];
  sortConfig: {
    key: keyof Invoice | null;
    direction: 'asc' | 'desc' | 'none';
  };
  handleSort: (key: keyof Invoice) => void;
  renderSortIcon: (key: keyof Invoice) => React.ReactNode;
  toggleSelectInvoice: (invoiceId: string) => void;
  toggleSelectAll: (filteredInvoices: Invoice[]) => void;
  handleBulkPayment: () => void;
  handleBulkCancel: () => void;
  handleBulkExport: () => void;
  updatePaymentDate: (invoiceId: string, date: Date) => void;
  handleRowClick: (invoice: Invoice) => void;
  handleSingleCancel: (invoiceId: string) => void;
  handleSingleExport: (invoiceId: string) => void;
  handleDownloadInvoice: (invoiceId: string, fileName: string) => void;
  showTabs?: boolean;
  statusFilter?: string[];
  refreshData?: () => Promise<void>;
  /** Pass-through — see BankAccountsStatus's docstring (#498 whole-branch review finding I2). */
  hasBankAccounts?: BankAccountsStatus;
}

const AccountPayablesContent: React.FC<AccountPayablesContentProps> = ({
  invoices,
  setInvoices,
  selectedInvoices,
  sortConfig,
  handleSort,
  renderSortIcon,
  toggleSelectInvoice,
  toggleSelectAll,
  handleBulkPayment,
  handleBulkCancel,
  handleBulkExport,
  updatePaymentDate,
  handleRowClick,
  handleSingleCancel,
  handleSingleExport,
  handleDownloadInvoice,
  showTabs = true,
  statusFilter,
  refreshData,
  hasBankAccounts
}) => {
  const t = useTranslations("consultantPayables.page");
  const getStatusBadge = useStatusBadge();
  // Use custom hooks for actions
  const {
    handleSinglePay,
    handleConfirmMatch,
    handleEditPayment,
    handleEditVendor,
    handleEditInvoice,
    editPaymentDialogOpen,
    setEditPaymentDialogOpen,
    selectedInvoiceForEdit,
    handlePaymentUpdateSuccess
  } = useInvoiceActions(invoices, setInvoices, refreshData);
  
  // Filter invoices by statusFilter if provided
  const filteredByStatus = React.useMemo(() => {
    if (statusFilter && statusFilter.length > 0) {
      return invoices.filter(inv => statusFilter.includes(inv.status));
    }
    return invoices;
  }, [invoices, statusFilter]);

  // Use the filtering hook
  const {
    activeTab,
    setActiveTab,
    filters,
    activeFilters,
    handleFilterChange,
    handleStatusFilterChange,
    resetFilters,
    applyFilters,
    showFilters,
    setShowFilters,
    uniqueCurrencies,
    filteredInvoices,
    sortedInvoices
  } = useAccountPayablesFiltering(filteredByStatus);

  // Define a wrapper function for toggleSelectAll to match expected signature
  const handleToggleSelectAll = () => {
    toggleSelectAll(filteredInvoices);
  };

  return (
    <>
      <TitledCard title={t("title")} description={t("subtitle")}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {showTabs && (
              <TabsNavigation 
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
            )}
            <TabContent 
              activeTab={activeTab}
              selectedInvoices={selectedInvoices}
              filteredInvoices={filteredInvoices}
              sortedInvoices={sortedInvoices}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              filters={filters}
              activeFilters={activeFilters}
              handleFilterChange={handleFilterChange}
              handleStatusFilterChange={handleStatusFilterChange}
              resetFilters={resetFilters}
              applyFilters={applyFilters}
              uniqueCurrencies={uniqueCurrencies}
              toggleSelectInvoice={toggleSelectInvoice}
              toggleSelectAll={handleToggleSelectAll}
              handleSort={handleSort}
              renderSortIcon={renderSortIcon}
              handleBulkPayment={handleBulkPayment}
              handleBulkCancel={handleBulkCancel}
              handleBulkExport={handleBulkExport}
              updatePaymentDate={updatePaymentDate}
              onRowClick={handleRowClick}
              handleSinglePay={handleSinglePay}
              handleConfirmMatch={handleConfirmMatch}
              handleSingleCancel={handleSingleCancel}
              handleSingleExport={handleSingleExport}
              handleDownloadInvoice={handleDownloadInvoice}
              onEditPayment={handleEditPayment}
              onEditVendor={handleEditVendor}
              onEditInvoice={handleEditInvoice}
              allInvoices={invoices}
              getStatusBadge={getStatusBadge}
              formatCurrency={formatCurrency}
              hasBankAccounts={hasBankAccounts}
            />
          </Tabs>
      </TitledCard>

      {/* Edit Payment Dialog */}
      <EditPaymentDialog
        open={editPaymentDialogOpen}
        onOpenChange={setEditPaymentDialogOpen}
        invoice={selectedInvoiceForEdit}
        onSuccess={handlePaymentUpdateSuccess}
      />
    </>
  );
};

export default AccountPayablesContent;
